import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Send, Loader2, Image as ImageIcon, ChevronDown, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppUser, TesterMessage, TesterThread } from '../types';
import {
  markTesterThreadAsRead, sendTesterMessage, subscribeToTesterMessages,
  subscribeToTesterThread, translateTesterMessage, getTesterLanguage,
} from '../services/firebaseService';

interface TesterInboxModalProps {
  currentUser: AppUser;
  isAdmin: boolean;
  testerUid?: string;
  testerName?: string;
  testerEmail?: string;
  testerPhotoURL?: string | null;
  onClose: () => void;
  initialMessage?: string;
  initialCategory?: Category;
}

const CATEGORIES = ['bug', 'improvement', 'content', 'other'] as const;
type Category = typeof CATEGORIES[number];

const categoryStyle: Record<Category, { active: string; label: string }> = {
  bug: { active: 'bg-red-500/20 border-red-500/40 text-red-300', label: 'feedback.categories.bug' },
  improvement: { active: 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300', label: 'feedback.categories.improvement' },
  content: { active: 'bg-blue-500/20 border-blue-500/40 text-blue-300', label: 'feedback.categories.content' },
  other: { active: 'bg-stone-500/20 border-stone-400/40 text-[var(--text-primary)]', label: 'feedback.categories.other' },
};

const LANG_LABELS: Record<string, string> = {
  no: 'Norsk', en: 'English', de: 'Deutsch', pl: 'Polski',
  es: 'Español', fr: 'Français', nl: 'Nederlands', sv: 'Svenska',
  da: 'Dansk', fi: 'Suomi', is: 'Íslenska',
};

const ReadReceiptAvatar: React.FC<{ photoURL?: string | null; name?: string }> = ({ photoURL, name }) => {
  const initial = (name || '?')[0].toUpperCase();
  return (
    <div className="w-4 h-4 rounded-full overflow-hidden bg-[var(--bg-surface)] border border-[var(--border-secondary)] shrink-0" title={name || ''}>
      {photoURL ? (
        <img src={photoURL} alt="" className="w-full h-full object-cover" />
      ) : (
        <span className="flex items-center justify-center w-full h-full text-[7px] font-black text-[var(--text-muted)]">{initial}</span>
      )}
    </div>
  );
};

const TesterInboxModal: React.FC<TesterInboxModalProps> = ({
  currentUser, isAdmin, testerUid, testerName, testerEmail, testerPhotoURL, onClose,
  initialMessage, initialCategory,
}) => {
  const { t, i18n } = useTranslation();
  const resolvedTesterUid = isAdmin ? (testerUid || '') : currentUser.uid;

  // My preferred language for receiving translations.
  // Non-admins translate into the language they currently have selected in the app
  // (i18n), which is the source of truth — it can differ from the persisted
  // currentUser.language when set via browser/IP detection or a forced-language param.
  const appLang = (i18n.resolvedLanguage || i18n.language || '').slice(0, 2);
  const myLang = isAdmin
    ? 'no'
    : (appLang || (currentUser as any).language || 'no');

  const [messages, setMessages] = useState<TesterMessage[]>([]);
  const [thread, setThread] = useState<TesterThread | null>(null);
  const [text, setText] = useState(initialMessage || '');
  const [category, setCategory] = useState<Category>(initialCategory || 'improvement');
  const [files, setFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCategories, setShowCategories] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Translation state
  const [translations, setTranslations] = useState<Record<string, string>>({});  // `${msgId}_${lang}` → text
  const [preferOriginal, setPreferOriginal] = useState<Set<string>>(new Set());   // msgIds showing original
  const [translatingIds, setTranslatingIds] = useState<Set<string>>(new Set());
  const [translateError, setTranslateError] = useState<string | null>(null);

  // Admin: outgoing translation
  const [testerLanguage, setTesterLanguage] = useState<string | null>(null);
  const [translateOutgoing, setTranslateOutgoing] = useState(false);

  useEffect(() => {
    if (!resolvedTesterUid) return;
    const unsubMsgs = subscribeToTesterMessages(resolvedTesterUid, (msgs) => {
      setMessages(msgs);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    });
    const unsubThread = subscribeToTesterThread(resolvedTesterUid, setThread);
    markTesterThreadAsRead(resolvedTesterUid, isAdmin ? 'admin' : 'tester').catch(() => { });
    return () => { unsubMsgs(); unsubThread(); };
  }, [resolvedTesterUid, isAdmin]);

  // Fetch tester's preferred language (admin only, for outgoing translation)
  useEffect(() => {
    if (isAdmin && resolvedTesterUid) {
      getTesterLanguage(resolvedTesterUid).then(setTesterLanguage).catch(() => { });
    }
  }, [isAdmin, resolvedTesterUid]);

  useEffect(() => {
    if (!resolvedTesterUid || messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.senderId !== currentUser.uid) {
      markTesterThreadAsRead(resolvedTesterUid, isAdmin ? 'admin' : 'tester').catch(() => { });
    }
  }, [messages.length]);

  const otherPartyLastRead = isAdmin
    ? (thread?.lastReadByTester ?? 0)
    : (thread?.lastReadByAdmin ?? 0);
  const otherPartyPhoto = isAdmin ? (testerPhotoURL ?? thread?.testerPhotoURL) : currentUser.photoURL;
  const otherPartyName = isAdmin ? (testerName || thread?.testerName) : 'Admin';

  const lastReadOwnIdx = useMemo(() => {
    let idx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].senderId === currentUser.uid && messages[i].createdAtMs <= otherPartyLastRead) {
        idx = i;
        break;
      }
    }
    return idx;
  }, [messages, currentUser.uid, otherPartyLastRead]);

  const handleTranslate = async (msg: TesterMessage) => {
    if (!msg.text) return;
    const cacheKey = `${msg.id}_${myLang}`;
    setTranslateError(null);

    // Already translated — just toggle visibility
    if (translations[cacheKey]) {
      setPreferOriginal(prev => {
        const next = new Set(prev);
        if (next.has(msg.id)) next.delete(msg.id); else next.add(msg.id);
        return next;
      });
      return;
    }

    setTranslatingIds(prev => new Set([...prev, msg.id]));
    try {
      const translated = await translateTesterMessage(msg.text, myLang);
      setTranslations(prev => ({ ...prev, [cacheKey]: translated }));
      setPreferOriginal(prev => { const next = new Set(prev); next.delete(msg.id); return next; });
    } catch {
      setTranslateError(t('feedback.translate_error'));
    } finally {
      setTranslatingIds(prev => { const next = new Set(prev); next.delete(msg.id); return next; });
    }
  };

  const toggleOriginal = (msgId: string) => {
    setPreferOriginal(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId); else next.add(msgId);
      return next;
    });
  };

  // Determine what text to display for a given message
  const getDisplayInfo = (m: TesterMessage): { displayText: string; isTranslated: boolean } => {
    const isOtherParty = m.senderId !== currentUser.uid;
    if (!isOtherParty) return { displayText: m.text, isTranslated: false };

    const cacheKey = `${m.id}_${myLang}`;
    const manualTranslation = translations[cacheKey];
    const preTranslation = (!isAdmin && m.translatedText && m.translatedTo === myLang) ? m.translatedText : undefined;
    const showOrig = preferOriginal.has(m.id);

    if (!showOrig) {
      if (manualTranslation) return { displayText: manualTranslation, isTranslated: true };
      if (preTranslation) return { displayText: preTranslation, isTranslated: true };
    }
    return { displayText: m.text, isTranslated: false };
  };

  const handleSend = async () => {
    if (!resolvedTesterUid || isSending || (!text.trim() && files.length === 0)) return;
    setIsSending(true);
    setError(null);
    try {
      let translationPayload: { translatedText: string; translatedTo: string } | undefined;
      if (isAdmin && translateOutgoing && testerLanguage && testerLanguage !== 'no' && text.trim()) {
        const translated = await translateTesterMessage(text.trim(), testerLanguage);
        translationPayload = { translatedText: translated, translatedTo: testerLanguage };
      }
      const result = await sendTesterMessage(
        resolvedTesterUid, currentUser,
        isAdmin ? 'admin' : 'tester',
        text, files, category,
        { testerName, testerEmail, testerPhotoURL },
        translationPayload,
      );
      setText('');
      setFiles([]);
      if (!isAdmin) setCategory('improvement');
      setShowCategories(false);
      // Teksten gikk gjennom, men et vedlegg ble ikke med. Uten dette tror
      // avsenderen at bildet fulgte med og venter på svar som aldri gir mening.
      if (result?.skipped?.length) {
        setError(t('feedback.error_images_skipped', { files: result.skipped.join(', ') }));
      }
    } catch (e: any) {
      // Meldingen var kun bilder, og ingen av dem kunne sendes. Tekst og filer
      // beholdes i skrivefeltet så hun kan prøve på nytt med et annet bilde.
      if (e?.code === 'image/unsendable') {
        setError(t('feedback.error_images_failed', { files: (e.skipped || []).join(', ') }));
      } else {
        setError(t('feedback.error_failed', { error: e?.code || 'unknown' }));
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && e.ctrlKey) { e.preventDefault(); handleSend(); }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData?.items || []);
    const pastedImages = items
      .filter((it) => it.kind === 'file' && it.type.startsWith('image/'))
      .map((it) => it.getAsFile())
      .filter((f): f is File => f !== null);
    if (pastedImages.length > 0) {
      e.preventDefault();
      setFiles((prev) => [...prev, ...pastedImages]);
    }
  };

  const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const filePreviews = useMemo(
    () => files.map((f) => ({ name: f.name, url: URL.createObjectURL(f) })),
    [files]
  );
  useEffect(() => {
    return () => { filePreviews.forEach((p) => URL.revokeObjectURL(p.url)); };
  }, [filePreviews]);

  const showOutgoingTranslateToggle = isAdmin && testerLanguage && testerLanguage !== 'no';

  return (
    <div className="fixed inset-0 z-[10030] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />

      <div className="relative w-full sm:max-w-lg flex flex-col bg-[var(--bg-base)] border border-[var(--border-secondary)] sm:rounded-panel rounded-t-panel shadow-3xl overflow-hidden"
        style={{ maxHeight: '92dvh' }}>

        {/* drag handle (mobile only) */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 bg-[var(--bg-surface)] rounded-full" />
        </div>

        {/* Header */}
        <div className="px-5 pb-4 pt-2 border-b border-[var(--border-secondary)] flex items-start justify-between shrink-0">
          <div className="flex-1 min-w-0 pr-2">
            <h3 className="text-[var(--text-primary)] text-base font-black uppercase tracking-tight leading-tight">
              {isAdmin
                ? t('feedback.tester_dialog', { name: testerName || thread?.testerName || testerUid })
                : t('feedback.improvement_suggestion')}
            </h3>
            {isAdmin && testerEmail && (
              <p className="text-tiny text-[var(--text-muted)] mt-0.5 truncate">{testerEmail}</p>
            )}
            {!isAdmin && (
              <p className="text-tiny text-[var(--text-muted)] mt-0.5">{t('feedback.inbox_description')}</p>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-xl bg-[var(--bg-surface-alt)] text-[var(--text-muted)] hover:text-[var(--text-primary)] shrink-0">
            <X size={18} />
          </button>
        </div>

        {/* Message list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[var(--bg-input)] overscroll-contain">
          {messages.length === 0 ? (
            <p className="text-xs text-[var(--text-muted)] text-center pt-10">{t('feedback.no_messages')}</p>
          ) : (
            messages.map((m, idx) => {
              const own = m.senderId === currentUser.uid;
              const showReadReceipt = own && idx === lastReadOwnIdx;
              const { displayText, isTranslated } = getDisplayInfo(m);
              const isOtherParty = !own;
              const isTranslating = translatingIds.has(m.id);
              const cacheKey = `${m.id}_${myLang}`;
              const hasTranslation = !!translations[cacheKey];
              const hasPreTranslation = !isAdmin && !!m.translatedText && m.translatedTo === myLang;
              const showingOrig = preferOriginal.has(m.id);
              const canTranslate = isOtherParty && !!m.text;

              return (
                <div key={m.id}>
                  <div className={`flex ${own ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[82%] rounded-2xl p-3 border ${own ? 'bg-blue-600/20 border-blue-500/30 text-[var(--text-primary)]' : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] text-[var(--text-primary)]'}`}>
                      <p className="text-3xs uppercase tracking-widest font-black opacity-60 mb-1">{m.senderName}</p>
                      {m.category && (
                        <p className="text-4xs uppercase tracking-widest font-black opacity-50 mb-1">
                          {t(categoryStyle[m.category as Category]?.label || 'feedback.categories.other')}
                        </p>
                      )}
                      {m.text && <p className="text-sm whitespace-pre-wrap leading-relaxed">{displayText}</p>}

                      {/* Translation controls — other party's messages */}
                      {canTranslate && (
                        <div className="flex items-center gap-2 mt-1.5">
                          {isTranslating ? (
                            <Loader2 size={9} className="animate-spin text-[var(--text-muted)]" />
                          ) : isTranslated ? (
                            <>
                              <span className="text-3xs text-[var(--text-muted)] italic">{t('feedback.translated_badge')}</span>
                              <button
                                onClick={() => toggleOriginal(m.id)}
                                className="text-3xs text-blue-400 hover:text-blue-300 transition-colors"
                              >
                                {t('feedback.view_original')}
                              </button>
                            </>
                          ) : showingOrig && (hasTranslation || hasPreTranslation) ? (
                            <>
                              <span className="text-3xs text-[var(--text-muted)] italic">{t('feedback.original_badge')}</span>
                              <button
                                onClick={() => toggleOriginal(m.id)}
                                className="text-3xs text-blue-400 hover:text-blue-300 transition-colors"
                              >
                                {t('feedback.view_translated')}
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleTranslate(m)}
                              className="flex items-center gap-1 text-3xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                            >
                              <Languages size={9} />
                              <span>{t('feedback.translate_btn')}</span>
                            </button>
                          )}
                        </div>
                      )}

                      {/* Admin sees their own pre-translated outgoing message */}
                      {own && isAdmin && m.translatedText && m.translatedTo && (
                        <p className="text-4xs text-[var(--text-muted)] mt-1.5 italic opacity-70">
                          → {LANG_LABELS[m.translatedTo] || m.translatedTo}: {m.translatedText.slice(0, 80)}{m.translatedText.length > 80 ? '…' : ''}
                        </p>
                      )}

                      {m.imageUrls?.length > 0 && (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {m.imageUrls.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                              className="block rounded-xl overflow-hidden border border-[var(--border-secondary)]">
                              <img src={url} alt="Vedlegg" className="w-full h-24 object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  {showReadReceipt && (
                    <div className="flex items-center justify-end gap-1 mt-1 mr-0.5">
                      <span className="text-2xs text-[var(--text-muted)]">{t('feedback.read')}</span>
                      <ReadReceiptAvatar photoURL={otherPartyPhoto} name={otherPartyName} />
                    </div>
                  )}
                </div>
              );
            })
          )}
          {translateError && (
            <p className="text-2xs text-red-400 text-center">{translateError}</p>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="border-t border-[var(--border-secondary)] bg-[var(--bg-base)] shrink-0">
          {/* Category picker — collapsible */}
          {showCategories && (
            <div className="grid grid-cols-4 gap-1.5 px-4 pt-3">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => { setCategory(cat); setShowCategories(false); }}
                  className={`py-2 rounded-xl text-3xs font-black uppercase tracking-wide border transition-all ${category === cat ? categoryStyle[cat].active : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] text-[var(--text-secondary)]'}`}
                >
                  {t(categoryStyle[cat].label)}
                </button>
              ))}
            </div>
          )}

          {/* Outgoing translation toggle (admin only) */}
          {showOutgoingTranslateToggle && (
            <div className="px-4 pt-2.5 flex items-center">
              <button
                type="button"
                onClick={() => setTranslateOutgoing(v => !v)}
                className={`flex items-center gap-1.5 text-2xs font-bold uppercase tracking-wide px-2.5 py-1.5 rounded-lg border transition-all ${
                  translateOutgoing
                    ? 'bg-blue-500/20 border-blue-500/40 text-blue-300'
                    : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] text-[var(--text-muted)]'
                }`}
              >
                <Languages size={11} />
                <span>
                  {translateOutgoing
                    ? t('feedback.translate_outgoing_on', { lang: LANG_LABELS[testerLanguage!] || testerLanguage })
                    : t('feedback.translate_outgoing_off', { lang: LANG_LABELS[testerLanguage!] || testerLanguage })}
                </span>
              </button>
            </div>
          )}

          {/* Textarea + actions */}
          <div className="p-3 flex items-end gap-2">
            <button
              type="button"
              onClick={() => setShowCategories(v => !v)}
              className={`shrink-0 p-2.5 rounded-xl border text-3xs font-black uppercase transition-all flex items-center gap-1 ${categoryStyle[category].active} ${showCategories ? 'ring-1 ring-white/20' : ''}`}
            >
              <span className="hidden xs:block">{t(categoryStyle[category].label)}</span>
              <ChevronDown size={12} className={`transition-transform ${showCategories ? 'rotate-180' : ''}`} />
            </button>

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={t('feedback.textarea_placeholder')}
              rows={2}
              className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-xl p-2.5 text-sm text-[var(--text-primary)] focus:border-blue-500 outline-none resize-none leading-relaxed"
            />

            <label className="shrink-0 cursor-pointer p-2.5 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all">
              <ImageIcon size={16} />
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
            </label>

            <button
              onClick={handleSend}
              disabled={isSending || (!text.trim() && files.length === 0)}
              className="shrink-0 p-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white transition-all"
            >
              {isSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </div>

          {(files.length > 0 || error) && (
            <div className="px-4 pb-3 space-y-2">
              {filePreviews.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {filePreviews.map((p, idx) => (
                    <div key={idx} className="relative w-14 h-14 rounded-lg overflow-hidden border border-[var(--border-secondary)] bg-[var(--bg-surface-alt)]">
                      <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black/90"
                        aria-label={t('common.remove', 'Fjern')}
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {error && <p className="text-2xs text-red-400">{error}</p>}
            </div>
          )}

          <div className="h-safe-area-bottom sm:h-0" style={{ height: 'env(safe-area-inset-bottom)' }} />
        </div>
      </div>
    </div>
  );
};

export default TesterInboxModal;
